/**
 * SimpleProductForm - Basic product creation form without AI
 * 
 * Creates a bare product with just name and description.
 * No AI extraction, no personas, no goals - just an empty product shell.
 */

import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Plus, Loader2, Sparkles } from 'lucide-react';
import Sidebar from '../components/layout/Sidebar';
import Header from '../components/layout/Header';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { cn } from '../lib/utils';
import { useSafeMode } from '../contexts/SafeModeContext';
import { productsApi } from '../services/api';
import { toast } from 'sonner';

const SimpleProductForm = () => {
  const navigate = useNavigate();
  const { getThemeColors } = useSafeMode();
  const colors = getThemeColors();
  
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    if (!name.trim()) {
      setError('Product name is required');
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const response = await productsApi.create({
        name: name.trim(),
        high_level_vision: description.trim() || '',
        skip_ai_extraction: true
      });
      
      toast.success('Product created successfully!');
      
      // Navigate to the new product
      if (response.data?.product_id) {
        navigate(`/products/${response.data.product_id}`);
      } else {
        navigate('/products');
      }
    } catch (err) {
      console.error('Error creating product:', err);
      const errorMessage = err.response?.data?.detail || 'Failed to create product';
      setError(typeof errorMessage === 'string' ? errorMessage : errorMessage.message || 'Failed to create product');
      toast.error('Failed to create product');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={cn("flex h-screen", colors.bgPrimary)}>
      <Sidebar />
      <div className="flex-1 flex flex-col overflow-hidden">
        <Header title="Create Product" />
        <div className="flex-1 overflow-y-auto p-8">
          <div className="max-w-xl mx-auto">
            {/* Back link */}
            <Link 
              to="/products/new" 
              className={cn("inline-flex items-center gap-2 text-sm mb-6 hover:underline", colors.textMuted)}
            >
              <ArrowLeft className="w-4 h-4" />
              Back to AI-powered creation
            </Link>
            
            <Card className={cn("p-8 border", colors.bgCard, colors.borderPrimary)}>
              <div className="mb-6">
                <h1 className={cn("text-2xl font-bold mb-2", colors.textPrimary)}>
                  Create Product from Scratch
                </h1>
                <p className={cn("text-sm", colors.textMuted)}>
                  Create an empty product and add details manually later.
                </p>
              </div>
              
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Product Name */}
                <div>
                  <Label htmlFor="name" className={cn("text-sm font-medium", colors.textPrimary)}>
                    Product Name <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="e.g., Customer Portal, Mobile App v2"
                    className={cn("mt-2", colors.bgInput, colors.borderPrimary, colors.textPrimary)}
                    disabled={isSubmitting}
                    autoFocus
                  />
                </div>
                
                {/* Description */}
                <div>
                  <Label htmlFor="description" className={cn("text-sm font-medium", colors.textPrimary)}>
                    Description <span className={cn("font-normal", colors.textMuted)}>(optional)</span>
                  </Label>
                  <Textarea
                    id="description"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Brief description of what this product is about..."
                    rows={4}
                    className={cn("mt-2", colors.bgInput, colors.borderPrimary, colors.textPrimary)}
                    disabled={isSubmitting}
                  />
                </div>
                
                {/* Error message */}
                {error && (
                  <div className="p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
                    <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
                  </div>
                )}
                
                {/* Submit button */}
                <div className="flex items-center justify-between pt-4">
                  <Link 
                    to="/products" 
                    className={cn("text-sm hover:underline", colors.textMuted)}
                  >
                    Cancel
                  </Link>
                  <Button
                    type="submit"
                    disabled={isSubmitting || !name.trim()}
                    className="gap-2"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Creating...
                      </>
                    ) : (
                      <>
                        <Plus className="w-4 h-4" />
                        Create Product
                      </>
                    )}
                  </Button>
                </div>
              </form>
              
              {/* AI suggestion */}
              <div className={cn("mt-8 pt-6 border-t", colors.borderPrimary)}>
                <div className="flex items-start gap-3">
                  <div className="p-2 rounded-lg bg-purple-100 dark:bg-purple-900/30">
                    <Sparkles className="w-4 h-4 text-purple-600 dark:text-purple-400" />
                  </div>
                  <div>
                    <p className={cn("text-sm font-medium", colors.textPrimary)}>
                      Want AI to help?
                    </p>
                    <p className={cn("text-xs mt-1", colors.textMuted)}>
                      Use the{' '}
                      <Link to="/products/new" className="text-blue-500 hover:underline">
                        AI-powered wizard
                      </Link>
                      {' '}to automatically generate personas, goals, and features from your description.
                    </p>
                  </div>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SimpleProductForm;
